/* LL: Local owns cart rendering, drawer focus and variant quick view. */
(() => {
  if (customElements.get('ll-card-add')) return;
  customElements.define('ll-card-add', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.addEventListener('submit', event => {
        event.preventDefault();
        if (!this.busy) this.add(event.target);
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    async add(form) {
      this.busy = true;
      const button = form.querySelector('button');
      const status = this.querySelector('[data-card-status]');
      const announcement = this.querySelector('[data-card-announcement]');
      const originalButtonHTML = this.originalButtonHTML || button.innerHTML;
      this.originalButtonHTML = originalButtonHTML;
      const items = [{ id: form.elements.id.value, quantity: 1 }];
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.hidden = true;
      status.textContent = '';
      announcement.textContent = '';
      let added = false;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || this.dataset.error);
        }
        added = true;
        button.textContent = 'Added ✓';
        status.hidden = true;
        status.textContent = '';
        announcement.textContent = this.dataset.added;
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => {
          button.innerHTML = originalButtonHTML;
          status.hidden = true;
          status.textContent = '';
          announcement.textContent = '';
        }, 2000);
        const url = new URL(location.href);
        url.searchParams.delete('view');
        url.searchParams.set('section_id', `${window.KT_FILE_PREFIX || ''}helper-cart`);
        const refresh = await fetch(url);
        if (!refresh.ok) throw new Error('Cart refresh failed');
        const html = await refresh.text();
        if (typeof window.renderHelperCart !== 'function') throw new Error('Cart refresh unavailable');
        window.renderHelperCart(html);
        document.dispatchEvent(new CustomEvent('ll:cart:rendered'));
        this.dispatchEvent(new Event('add-to-cart'));
        window.KROWN?.functions?.eventDispatcher?.('krown:cart:add', { productForm: null, variantId: items[0].id, items });
        window.KROWN?.functions?.dispatchStandardEvent?.('CartLinesUpdateEvent', this, {
          action: 'add', context: 'product', lines: items.map(item => ({ merchandiseId: String(item.id), quantity: 1 })), promise: Promise.resolve({ cart: null })
        });
        const recommendations = document.querySelector('cart-recommendations');
        if (typeof recommendations?.generateRecommendations === 'function') {
          recommendations.innerHTML = ''; recommendations.generateRecommendations();
        }
      } catch (error) {
        // A failed drawer refresh must never invite a duplicate add.
        if (added) {
          status.hidden = true;
          status.textContent = '';
          try {
            const fallback = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
            if (fallback.ok) {
              const cart = await fallback.json();
              document.querySelectorAll('[data-header-cart-count]').forEach(count => {
                count.textContent = String(cart.item_count || 0);
                count.hidden = !cart.item_count;
              });
            }
          } catch (_) {}
          document.dispatchEvent(new CustomEvent('ll:cart:rendered'));
        } else {
          announcement.textContent = '';
          status.hidden = false;
          status.textContent = error instanceof TypeError ? this.dataset.error : error.message;
        }
      } finally {
        this.busy = false; button.disabled = false; button.removeAttribute('aria-busy');
      }
    }
  });
})();
/* LL 88P2: collection-card offer progress; one cart read is shared by every visible card. */
(() => {
  if (customElements.get('ll-card-supplement-offer')) return;
  const offers = new Set();
  const eligibleProducts = new Set();
  const watchedCoreScripts = new WeakSet();
  let basket = null;
  let syncing = null;
  let scheduled = false;
  let syncAgain = false;

  const root = () => (window.Shopify?.routes?.root || [...offers][0]?.dataset.root || '/').replace(/\/?$/, '/');
  const lines = cart => (cart?.items || []).filter(item => !String(item.properties?._ll_gift ?? '').trim() && eligibleProducts.has(String(item.product_id))).map((item, index) => ({
    id: item.key || `${item.variant_id}-${index}`,
    price: item.original_price || item.price || item.final_price || 0,
    quantity: item.quantity
  }));
  const paint = () => {
    if (!basket || !window.LLSupplementOfferCore) return;
    const totals = window.LLSupplementOfferCore.calculate(lines(basket));
    offers.forEach(offer => offer.paint(totals));
  };
  const scheduleSync = () => {
    if (syncing) { syncAgain = true; return; }
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      sync().catch(() => {});
    });
  };
  const armCore = () => {
    if (window.LLSupplementOfferCore) return true;
    let scripts = [...document.querySelectorAll('script[src*="ll-supplement-offer-core"]')];
    if (!scripts.length) {
      const src = [...offers].map(offer => offer.dataset.coreSrc).find(Boolean);
      if (src) {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        document.head.append(script);
        scripts = [script];
      }
    }
    scripts.forEach(script => {
      if (watchedCoreScripts.has(script)) return;
      watchedCoreScripts.add(script);
      script.addEventListener('load', scheduleSync, { once: true });
    });
    return false;
  };
  async function sync() {
    if (!offers.size || !armCore()) return;
    if (syncing) { syncAgain = true; return syncing; }
    syncing = (async () => {
      const response = await fetch(`${root()}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Basket unavailable');
      basket = await response.json();
      paint();
    })();
    try { await syncing; }
    catch (_) { offers.forEach(offer => offer.unavailable()); }
    finally {
      syncing = null;
      if (syncAgain) { syncAgain = false; scheduleSync(); }
    }
  }

  customElements.define('ll-card-supplement-offer', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        JSON.parse(this.querySelector('[data-eligible-products]')?.textContent || '[]').forEach(id => eligibleProducts.add(String(id)));
      } catch (_) {}
      if (this.hidden !== (eligibleProducts.size === 0)) this.hidden = eligibleProducts.size === 0;
      offers.add(this);
      paint();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
      else scheduleSync();
      armCore();
    }
    disconnectedCallback() { offers.delete(this); this.ready = false; }
    paint(totals) {
      if (this.hidden) this.hidden = false;
      const position = totals.count ? (totals.count % 3 || 3) : 0;
      this.querySelectorAll('[data-offer-slot]').forEach((slot, index) => {
        const filled = index < position;
        if (slot.classList.contains('is-filled') !== filled) slot.classList.toggle('is-filled', filled);
      });
      const remaining = position === 3 ? 0 : 3 - position;
      const progress = position === 3
        ? `${totals.count} supplements in your basket. The latest trio has its free slot.`
        : `${totals.count} ${totals.count === 1 ? 'supplement' : 'supplements'} in your basket. Add ${remaining} more to complete the next trio.`;
      const count = String(totals.count), positionValue = String(position);
      if (this.dataset.offerCount !== count) this.dataset.offerCount = count;
      if (this.dataset.offerPosition !== positionValue) this.dataset.offerPosition = positionValue;
      const label = `Buy any 2, get the 3rd free. ${progress}`;
      if (this.getAttribute('aria-label') !== label) this.setAttribute('aria-label', label);
      const status = this.querySelector('[data-offer-status]');
      if (status?.textContent !== progress) status.textContent = progress;
    }
    unavailable() {
      if (this.hasAttribute('data-offer-count')) this.removeAttribute('data-offer-count');
      if (this.hasAttribute('data-offer-position')) this.removeAttribute('data-offer-position');
      const label = 'Buy any 2, get the 3rd free. Basket progress is temporarily unavailable.';
      if (this.getAttribute('aria-label') !== label) this.setAttribute('aria-label', label);
    }
  });

  for (const event of ['ll:cart:rendered', 'krown:cart:add', 'krown:cart:update', 'cart:updated']) {
    document.addEventListener(event, scheduleSync);
  }
  document.addEventListener('shopify:section:load', scheduleSync);
  window.addEventListener('pageshow', scheduleSync);
})();
/* LL: one shared basket read per sync, with serialized single-meal edits. */
(() => {
  // Kept local to this controller as well: the direct-add and meal-card controllers
  // are separate closures and must each be able to read the merchant copy safely.
  const llText = (key, fallback, values = []) => {
    let copy;
    if (typeof document !== 'undefined') {
      const node = document.getElementById('LLCopy');
      if (node) {
        if (!node.llCopy) { try { node.llCopy = JSON.parse(node.textContent); } catch (_) { node.llCopy = {}; } }
        copy = node.llCopy[key];
      }
    }
    return String(copy == null ? fallback : copy).replace(/\[(\d+)\]/g, (token, n) => n <= values.length ? String(values[n - 1]) : token);
  };
  if (customElements.get('ll-meal-card')) return;
  const cards = new Set();
  const root = () => window.Shopify?.routes?.root || '/';
  let basket = null;
  let syncing = null;
  let scheduled = false;
  let updating = false;
  let syncAgain = false;
  const defaults = { add: llText("ll_js_product_card_add", "Add"), minus: llText("ll_js_product_card_remove_one", "Remove one"), plus: llText("ll_js_product_card_add_one", "Add one"), quantity: llText("ll_js_product_card_count_in_basket", "[count] in basket"), error: llText("ll_js_product_card_couldn_t_update_your_basket_try_a", "Couldn't update your basket, try again"), sold: llText("ll_js_product_card_sold_out", "Sold out") };
  function labels() {
    try { return { ...defaults, ...JSON.parse(document.querySelector('[data-ll-meal-card-labels]')?.textContent || '{}') }; }
    catch (_) { return defaults; }
  }
  const lineFor = (cart, id) => cart?.items?.find(item => String(item.variant_id) === id && !Object.prototype.hasOwnProperty.call(item.properties || {}, '_ll_gift'));
  async function readCart() {
    const response = await fetch(`${root()}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Basket unavailable');
    return response.json();
  }
  function paint() { cards.forEach(card => card.paint()); }
  function scheduleSync() {
    if (updating || syncing) { syncAgain = true; return; }
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      sync().catch(() => {});
    });
  }
  async function sync() {
    if (updating) { syncAgain = true; return; }
    if (syncing) return syncing;
    syncing = (async () => {
      try { basket = await readCart(); }
      catch (error) {
        cards.forEach(card => card.showError());
        throw error;
      } finally { paint(); }
    })();
    try { await syncing; }
    finally {
      syncing = null;
      if (syncAgain) { syncAgain = false; scheduleSync(); }
    }
  }
  customElements.define('ll-meal-card', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      cards.add(this);
      this.paint();
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || button.disabled || updating) return;
        if (button.matches('[data-meal-add],[data-meal-plus],[data-meal-minus]')) {
          this.update(button.matches('[data-meal-add]') ? 'add' : button.matches('[data-meal-plus]') ? 1 : -1);
        }
      }, { signal: this.controller.signal });
      scheduleSync();
    }
    disconnectedCallback() { cards.delete(this); this.controller?.abort(); this.controller = null; }
    showError() { this.querySelector('[data-meal-error]').textContent = labels().error; }
    paint() {
      const text = labels();
      const count = lineFor(basket, this.dataset.variant)?.quantity || 0;
      const available = this.dataset.available !== 'false';
      const add = this.querySelector('[data-meal-add]');
      const minus = this.querySelector('[data-meal-minus]');
      const plus = this.querySelector('[data-meal-plus]');
      add.textContent = available ? text.add : text.sold;
      add.setAttribute('aria-label', `${add.textContent}: ${this.dataset.product}`);
      minus.setAttribute('aria-label', `${text.minus}: ${this.dataset.product}`);
      plus.setAttribute('aria-label', `${text.plus}: ${this.dataset.product}`);
      add.hidden = count > 0;
      this.querySelector('[data-meal-stepper]').hidden = count === 0;
      this.querySelector('[data-meal-quantity]').textContent = count;
      this.querySelector('[data-meal-announcement]').textContent = basket ? text.quantity.replaceAll('[count]', count) : '';
      this.querySelector('[aria-busy]').setAttribute('aria-busy', String(updating));
      // If a read failed, a deliberate click can try a fresh read before any write.
      add.disabled = updating || !available;
      minus.disabled = updating;
      plus.disabled = updating || !available;
    }
    async update(action) {
      updating = true;
      const active = document.activeElement;
      this.querySelector('[data-meal-error]').textContent = '';
      paint();
      try {
        // Finish an older read before resolving the current, non-gift line key.
        if (syncing) await syncing.catch(() => {});
        basket = await readCart();
        const line = lineFor(basket, this.dataset.variant);
        const adding = action === 'add';
        if (!adding && !line) return;
        const payload = adding ? { items: [{ id: this.dataset.variant, quantity: 1 }] } : { id: line.key, quantity: Math.max(0, line.quantity + action) };
        const response = await fetch(`${root()}cart/${adding ? 'add' : 'change'}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Basket update failed');
        const refresh = await fetch(`${root()}?section_id=${window.KT_FILE_PREFIX || ''}helper-cart`, { cache: 'no-store' });
        if (!refresh.ok || typeof window.renderHelperCart !== 'function') throw new Error('Basket refresh failed');
        window.renderHelperCart(await refresh.text());
        // Deliberately no drawer.show() or add-to-cart event for single meals.
      } catch (_) {
        this.showError();
      } finally {
        // Also recovers an accepted write whose response was lost. Never retry a write.
        syncAgain = false;
        try { basket = await readCart(); } catch (_) { this.showError(); }
        updating = false;
        paint();
        if (syncAgain) { syncAgain = false; scheduleSync(); }
        if (this.contains(active) && active.closest('[hidden]')) {
          this.querySelector(lineFor(basket, this.dataset.variant) ? '[data-meal-plus]' : '[data-meal-add]')?.focus({ preventScroll: true });
        }
      }
    }
  });
  document.addEventListener('ll:cart:rendered', scheduleSync);
  document.addEventListener('shopify:section:load', () => { paint(); scheduleSync(); });
  window.addEventListener('pageshow', event => { if (event.persisted) scheduleSync(); });
})();
